import { ApolloError } from '@apollo/client';
import type { Primitive } from 'type-fest';

import type { ErrorMessageTypes } from '../components/alerts/errors/error-map';
import { CONSTANTS } from '../constants';
import type { ChallengeQuestionsFormModel } from '../models/form.models';
import type { GenericApiResponse } from '../models/generic.api.response';
import type { SessionInformationApiResponse } from '../models/session-information.api.response';
import {
    CreateAccountMutation,
    type CreateAccountRequest,
} from '../mutation/create-account.mutation';
import { getTokenFromLocalStorageIfValid } from '../utils/get-token-from-local-storage';
import { setLocalStorageItem } from '../utils/local-storage/local-storage-operations';

import { getAccountErrorFromApolloError } from './get-account-error';
import { SsoApiClient } from './sso-api-client';

export type CreateAccountApiResponse = GenericApiResponse & {
    userId?: string;
    sessionId?: string;
};
export const createAccountApi = async (
    firstName: string,
    lastName: string,
    email: string,
    dateOfBirth: string,
    phoneNumber: string,
    password: string,
    masterId: string,
    address: ChallengeQuestionsFormModel['address'] | null = null,
    partnerAccountConfiguration?: Record<string, Primitive>,
    partnerId?: string
): Promise<SessionInformationApiResponse> => {
    let error: ErrorMessageTypes | undefined = '500_GenericError';
    let errorMessage: string | undefined;

    const token = getTokenFromLocalStorageIfValid();

    const createAccountRequest: CreateAccountRequest = {
        patient: {
            firstName,
            lastName,
            dateOfBirth,
            phoneNumber,
            email,
            password,
            address,
        },
        partnerAccountConfiguration,
        partnerId,
        deviceToken: token,
    };

    if (masterId !== '' && masterId !== undefined) {
        createAccountRequest.patientProfile = `patient/${masterId}`;
    }

    try {
        const { data } = await SsoApiClient.mutate({
            mutation: CreateAccountMutation,
            errorPolicy: 'all',
            variables: {
                requestBody: createAccountRequest,
            },
        });

        const kratosSessionId = data?.createAccountResponse?.kratosSessionId;

        if (kratosSessionId) {
            setLocalStorageItem(CONSTANTS.sessionKey, kratosSessionId);
            return { success: true, sessionId: kratosSessionId };
        }

        if (data?.createAccountResponse) {
            // patient-agent answers 200 with a null kratosSessionId when the
            // member already has a Kratos identity: AccountService only assigns
            // a session on the two branches that create one. That is a failure
            // for us — the caller would otherwise be sent to a redirect it has
            // no session to complete. No ErrorMessageTypes code fits it, so it
            // reports as generic and the reason travels in errorMessage.
            return {
                success: false,
                error: '500_GenericError',
                errorMessage: 'Account created without a Kratos session id',
            };
        }
    } catch (err) {
        const createAccountError = err as ApolloError;
        const errorResult = createAccountError
            ? getAccountErrorFromApolloError(createAccountError)
            : { errorType: '500_GenericError' as ErrorMessageTypes };
        error = errorResult.errorType;
        errorMessage = errorResult?.errorMessage;
    }
    return { success: false, error, errorMessage };
};
